import ButtonIcon from "components/ButtonIcon/ButtonIcon.tsx";
import { KaboodleToaster } from "components/common";
import { Tooltip } from "components/Tooltip/Tooltip";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { api } from "services/api.ts";
import {
  useGetCostProjectionByIdQuery,
  usePatchCostProjectionByIdMutation,
} from "services/cost-projection-api";
import { useGetMSAByIdQuery, usePatchMSAByIdMutation } from "services/msa-api";

type FutureMedicalReviewButtonsProps = {
  msaId?: string;
  amendedReviewId?: string;
  mcpId?: string;
  lcpId?: string;
};

/*
this component displays a button that switches between "Incomplete" and "Complete" status for the Medical and Legal Reviews
*/
const FutureMedicalReviewButtons = ({
  msaId,
  amendedReviewId,
  mcpId,
  lcpId,
}: FutureMedicalReviewButtonsProps) => {
  const location = useLocation();
  const url = location.pathname;

  // determine current page context
  const isMSAMedicalReviewPage =
    url.includes("/future-medical/msa") && url.includes("/medical-review");
  const isMSALegalReviewPage =
    url.includes("/future-medical/msa") && url.includes("/legal-review");
  const isAmendedReviewMedicalReviewPage =
    url.includes("/future-medical/amended-review") &&
    url.includes("/medical-review");
  const isAmendedReviewLegalReviewPage =
    url.includes("/future-medical/amended-review") &&
    url.includes("/legal-review");
  const isCostProjectionMedicalReviewPage =
    (url.includes("/future-medical/mcp/") ||
      url.includes("/future-medical/lcp/")) &&
    url.includes("/medical-review");
  const isCostProjectionLegalReviewPage =
    (url.includes("/future-medical/mcp/") ||
      url.includes("/future-medical/lcp/")) &&
    url.includes("/legal-review");

  // get MSA for review pages
  const shouldFetchMSAForReview = Boolean(
    (isMSAMedicalReviewPage ||
      isMSALegalReviewPage ||
      isAmendedReviewMedicalReviewPage ||
      isAmendedReviewLegalReviewPage) &&
    (msaId || amendedReviewId),
  );
  const { data: msaReviewData } = useGetMSAByIdQuery(
    {
      msaId: msaId || amendedReviewId,
    },
    {
      skip: !shouldFetchMSAForReview,
    },
  );

  // get Cost Projection for review pages
  const shouldFetchCostProjectionForReview = Boolean(
    (isCostProjectionMedicalReviewPage || isCostProjectionLegalReviewPage) &&
    (mcpId || lcpId),
  );
  const { data: costProjectionReviewData } = useGetCostProjectionByIdQuery(
    {
      costProjectionId: mcpId || lcpId,
    },
    {
      skip: !shouldFetchCostProjectionForReview,
    },
  );

  const dispatch = useDispatch();
  const [patchMSAById] = usePatchMSAByIdMutation();
  const [patchCostProjectionById] = usePatchCostProjectionByIdMutation();

  // handlers for future medical review completion
  const handleReviewCompletion = useCallback(
    async (
      field: "medical_review_is_complete" | "legal_review_is_complete",
      value: boolean,
    ) => {
      try {
        // determine which service and mutation to use
        if (msaReviewData) {
          await patchMSAById({
            msaId: msaId || amendedReviewId,
            data: { [field]: value },
          }).unwrap();
        } else if (costProjectionReviewData) {
          await patchCostProjectionById({
            costProjectionId: mcpId || lcpId,
            data: { [field]: value },
          }).unwrap();
        }

        // when medical review is marked complete, the backend clones allocations
        // to legal review — invalidate so legal review picks them up
        if (field === "medical_review_is_complete" && value) {
          dispatch(
            api.util.invalidateTags([
              "TreatmentAllocation",
              "PrescriptionAllocation",
            ]),
          );
        }

        // construct message based on field and value
        const reviewType =
          field === "medical_review_is_complete" ? "Medical" : "Legal";
        const message = value
          ? `${reviewType} Review marked as complete`
          : `${reviewType} Review marked as incomplete`;

        KaboodleToaster.success(message);
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
        KaboodleToaster.error(`Failed to update review status`);
      }
    },
    [
      amendedReviewId,
      costProjectionReviewData,
      dispatch,
      lcpId,
      mcpId,
      msaId,
      msaReviewData,
      patchCostProjectionById,
      patchMSAById,
    ],
  );

  // determine which buttons to show
  const showMedicalReviewButton = useMemo(() => {
    return (
      (isMSAMedicalReviewPage && msaReviewData) ||
      (isCostProjectionMedicalReviewPage && costProjectionReviewData) ||
      (isAmendedReviewMedicalReviewPage && msaReviewData)
    );
  }, [
    isMSAMedicalReviewPage,
    isCostProjectionMedicalReviewPage,
    isAmendedReviewMedicalReviewPage,
    msaReviewData,
    costProjectionReviewData,
  ]);

  const showLegalReviewButton = useMemo(() => {
    return (
      (isMSALegalReviewPage && msaReviewData) ||
      (isCostProjectionLegalReviewPage && costProjectionReviewData) ||
      (isAmendedReviewLegalReviewPage && msaReviewData)
    );
  }, [
    isMSALegalReviewPage,
    isCostProjectionLegalReviewPage,
    isAmendedReviewLegalReviewPage,
    msaReviewData,
    costProjectionReviewData,
  ]);

  // get the appropriate data based on context
  const reviewData = msaReviewData || costProjectionReviewData;

  if (!reviewData) return null;

  return (
    <>
      {showMedicalReviewButton &&
        reviewData.medical_review_is_complete !== undefined && (
          <Tooltip
            content={
              reviewData.medical_review_is_complete
                ? "Mark Medical Review Incomplete"
                : "Mark Medical Review Complete"
            }
            variant={reviewData.medical_review_is_complete ? "dark" : "success"}
          >
            <ButtonIcon
              kind={
                reviewData.medical_review_is_complete ? "success" : "neutral"
              }
              onClick={() =>
                handleReviewCompletion(
                  "medical_review_is_complete",
                  !reviewData.medical_review_is_complete,
                )
              }
            >
              medical_information
            </ButtonIcon>
          </Tooltip>
        )}

      {showLegalReviewButton &&
        reviewData.legal_review_is_complete !== undefined && (
          <Tooltip
            content={
              reviewData.legal_review_is_complete
                ? "Mark Legal Review Incomplete"
                : "Mark Legal Review Complete"
            }
            variant={reviewData.legal_review_is_complete ? "dark" : "success"}
          >
            <ButtonIcon
              kind={reviewData.legal_review_is_complete ? "success" : "neutral"}
              onClick={() =>
                handleReviewCompletion(
                  "legal_review_is_complete",
                  !reviewData.legal_review_is_complete,
                )
              }
            >
              gavel
            </ButtonIcon>
          </Tooltip>
        )}
    </>
  );
};

export default FutureMedicalReviewButtons;
